/**
 * User Management Screen
 *
 * Allows admins and superadmins to view and manage users in their organization.
 * Features include user listing, role filtering, and password reset functionality.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { useLocalFilter } from '@/hooks/useLocalFilter';
import { SessionKey } from '@/types/storage';
import { SearchInput } from '@/components/core';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useScreenState } from '@/hooks/useScreenState';
import { useAlert } from '@/components/core/Alert';
import { useOptimisticAction } from '@/hooks/useOptimisticAction';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { ScreenType } from '@/types/screen.types';
import { UserRole } from '@/types/enums/UserRole';
import {
  OrganizationUser,
  UserTableFilters,
} from '@/types/user-management.types';
import { UserTable } from './UserTable';
import { UserDetailsDialog } from './UserDetailsDialog';
import { CreateUserDialog } from './CreateUserDialog';
import { Alert } from '@/components/core/Alert';
import { AsyncButton } from '@/components/ui';
import { IconName } from '@/icons';
import { logger } from '@/utils/logger';
import { getResponseCodeMessage } from '@/business/constants/response-codes';
import { useOrgUsersQuery } from '@/hooks/queries';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import type {
  DeleteUserInput,
  DeleteUserOutput,
  ResetUserPasswordInput,
  ResetUserPasswordOutput,
  UpdateUserInput,
  UpdateUserOutput,
} from '@/types/generated';

const USER_NAME_SEARCH_KEYS: Array<keyof OrganizationUser> = [
  'username',
  'display_name',
];

/**
 * User Management Screen Component
 */
export default function UserManagement() {
  const { activeOrganizationId } = useActiveOrganization();
  const alert = useAlert();

  // State
  const [roleFilter, setRoleFilter] = useSessionState<UserRole | 'all'>(
    SessionKey.LB_USER_MANAGEMENT_ROLE_FILTER,
    'all'
  );
  const filters: UserTableFilters = { roleFilter };
  const [nameSearch, setNameSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(
    null
  );
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const usersRef = useRef<OrganizationUser[]>([]);

  // TQ query for org users
  const { data: orgUsersData, isLoading: isLoadingOrgUsers } =
    useOrgUsersQuery(activeOrganizationId);

  // Derive users from query data (no duplicate state)
  const users = useMemo(() => orgUsersData?.users ?? [], [orgUsersData?.users]);

  // Keep ref in sync for optimistic updates
  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  // Reset password action
  const resetPasswordOrchestrator = useOrchestrator<
    ResetUserPasswordInput,
    ResetUserPasswordOutput
  >(Action.RESET_USER_PASSWORD);

  // Delete user action
  const { execute: executeDeleteUser } = useOrchestrator<
    DeleteUserInput,
    DeleteUserOutput
  >(Action.DELETE_USER);

  // Update user action with optimistic updates
  const { execute: executeUpdateUser, isLoading: isUpdatingUser } =
    useOptimisticAction<UpdateUserInput, UpdateUserOutput, OrganizationUser[]>({
      action: Action.UPDATE_USER,
      stateRef: usersRef,
      setState: newState => {
        // Patch TQ cache directly — users derived via useMemo will update automatically
        queryClient.setQueryData(
          queryKeys.orgUsers.forOrg(activeOrganizationId!),
          (prev: { users: OrganizationUser[] } | undefined) =>
            prev ? { ...prev, users: newState || [] } : prev
        );
      },
      optimisticUpdate: (input, currentUsers) => {
        // Update the user in the list optimistically
        return currentUsers.map(user =>
          user.id === input.user_id
            ? {
                ...user,
                username: input.username || user.username,
                display_name: input.display_name || user.display_name,
                email:
                  input.email !== undefined
                    ? (input.email ?? null)
                    : user.email,
                role: (input.role as UserRole) || user.role,
                profile_image:
                  input.profile_image !== undefined
                    ? input.profile_image || undefined
                    : user.profile_image,
              }
            : user
        );
      },
      onError: code => {
        // Show specific error messages
        const errorMessage =
          code === ResponseCode.CONFLICT
            ? 'This username is already taken. Please choose a different username.'
            : getResponseCodeMessage(code);

        alert.show({
          type: 'error',
          title: 'Update Failed',
          message: errorMessage,
          buttons: [{ label: 'OK', onClick: () => {} }],
        });
      },
    });

  // Screen state management — TQ handles data loading
  useScreenState({
    metadata: {
      screenType: ScreenType.MANAGE_PEOPLE,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, state) => {
      if (!activeOrganizationId) {
        logger.error('User Management: No organization_id found');
        state.setError({
          code: ResponseCode.NOT_FOUND,
          message: 'Organization not found',
          canRetry: false,
        });
        return;
      }
      logger.info('User Management: Loading users', {
        organization_id: activeOrganizationId,
      });
      state.setSuccess();
    },
    lifecycle: {
      onUnmount: () => {
        logger.info('User Management unmounted');
      },
    },
  });

  // Derive filtered users (no duplicate state)
  const roleFilteredUsers = useMemo(
    () =>
      filters.roleFilter === 'all'
        ? users
        : users.filter(u => u.role === filters.roleFilter),
    [users, filters.roleFilter]
  );
  const filteredUsers = [
    ...useLocalFilter(roleFilteredUsers, nameSearch, USER_NAME_SEARCH_KEYS),
  ].sort((a, b) => a.display_name.localeCompare(b.display_name));

  // Handle password reset
  const handlePasswordReset = async (
    userId: string,
    userEmail: string,
    newPassword: string
  ) => {
    const result = await resetPasswordOrchestrator.execute({
      user_id: userId,
      user_email: userEmail || undefined,
      new_password: newPassword || undefined,
    });

    if (result.code === ResponseCode.SUCCESS && result.data) {
      logger.info('User Management: Password reset successful', {
        user_id: userId,
        had_email: !!userEmail,
      });

      const successMessage = userEmail
        ? `The new password has been sent to ${userEmail}.`
        : 'The password has been updated successfully.';

      alert.show({
        type: 'success',
        title: 'Password Reset Successful',
        message: successMessage,
        buttons: [{ label: 'Got it', onClick: () => {} }],
      });
    } else {
      logger.error('User Management: Password reset failed', {
        userId,
        code: result.code,
      });

      alert.show({
        type: 'error',
        title: 'Password Reset Failed',
        message: getResponseCodeMessage(result.code),
        buttons: [{ label: 'OK', onClick: () => {} }],
      });
    }
  };

  // Handle role filter change
  const handleFilterChange = (value: string) => {
    setRoleFilter(value as UserRole | 'all');
  };

  // Handle row click to open user details dialog
  const handleRowClick = (user: OrganizationUser) => {
    setSelectedUser(user);
    setIsUserDialogOpen(true);
  };

  // Handle user update with optimistic updates (no reload needed!)
  const handleUserUpdate = async (updatedData: Partial<OrganizationUser>) => {
    if (!selectedUser) return;

    // Execute with optimistic update (UI updates immediately, auto toast)
    const result = await executeUpdateUser({
      user_id: selectedUser.id,
      username: updatedData.username,
      display_name: updatedData.display_name,
      email: updatedData.email ?? undefined,
      role: updatedData.role,
      profile_image: updatedData.profile_image ?? undefined,
    });

    if (result.code === ResponseCode.SUCCESS) {
      logger.info('User Management: User updated successfully', {
        user_id: selectedUser.id,
      });

      // Close dialog (list already updated optimistically, cache invalidated by side effect)
      setIsUserDialogOpen(false);
    } else {
      logger.error('User Management: User update failed', {
        user_id: selectedUser.id,
        code: result.code,
      });
      // Error toast already shown automatically, state rolled back
    }
  };

  // Handle delete user (soft-delete)
  const handleDeleteUser = (user: OrganizationUser) => {
    alert.show({
      type: 'warning',
      title: `Delete "${user.display_name}"?`,
      message: `This will soft-delete the user "${user.username}". It can be restored later.`,
      buttons: [
        { label: 'Cancel', variant: 'outline', onClick: () => {} },
        {
          label: 'Delete',
          variant: 'destructive',
          onClick: async () => {
            try {
              await executeDeleteUser({ user_id: user.id });
            } catch {
              // handled by orchestrator
            }
          },
        },
      ],
    });
  };

  return (
    <div className="space-y-4 p-6">
      {/* Filter section and hints */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Click column headers to sort. Click any row to view details.
        </div>

        {/* Filter section aligned to the right */}
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            Showing {filteredUsers.length} of {users.length} users
          </div>
          <SearchInput
            value={nameSearch}
            onChange={setNameSearch}
            placeholder="Search by name or username..."
            className="w-56"
          />
          <select
            id="role-filter"
            value={filters.roleFilter}
            onChange={e => handleFilterChange(e.target.value)}
            className="flex h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="all">All Users</option>
            <option value={UserRole.ADMIN}>Admins</option>
            <option value={UserRole.MENTOR}>Mentors</option>
            <option value={UserRole.STUDENT}>Students</option>
          </select>
          <AsyncButton
            onClick={async () => setIsCreateDialogOpen(true)}
            icon={IconName.PLUS}
          >
            Create User
          </AsyncButton>
        </div>
      </div>

      {/* User Table */}
      <UserTable
        users={filteredUsers}
        isLoading={isLoadingOrgUsers}
        onPasswordReset={handlePasswordReset}
        onRowClick={handleRowClick}
        onDelete={handleDeleteUser}
      />

      {/* User Details Dialog */}
      <UserDetailsDialog
        user={selectedUser}
        isOpen={isUserDialogOpen}
        isLoading={isUpdatingUser}
        onClose={() => setIsUserDialogOpen(false)}
        onSave={handleUserUpdate}
      />

      {/* Create User Dialog */}
      <CreateUserDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />

      {/* Alert Component */}
      <Alert {...alert.props} />
    </div>
  );
}
